import React, { Fragment, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles, Tooltip } from '@material-ui/core';
import { lowerCase, upperFirst } from 'lodash';
import HelpIcon from '@material-ui/icons/Help';

import { fetchOrganizations } from '../../../actions/organization';
import { getConfig, getUserDetails } from '../../../reducers/portalConfig';
import {
  getOrganizationsListTotalPages,
  getOrganizationsListTotalElements,
  getOrganizationsListResults,
  getIsListError,
  getIsLoading,
} from '../../../reducers/organization';
import styles from './styles';
import ListContainer from '../../list';
import { FilterByName, FilterByType } from './controls';
import {
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  ALERT_SUCCESS,
  ALERT_ERROR,
  ANY,
} from '../../../constants';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import {
  hasPublisherRole,
  hasAdminRole,
  hasOrgPublisherRole,
} from '../../../utils';

const formatType = (type) => upperFirst(lowerCase(type));

const renderTypeHeaderTooltip = (intl) =>
  (<div>
    <div>
      {`${intl.getI18nMessage('label.organization.list.page.filter.by.publisher.types')}:
        ${intl.getI18nMessage('label.organization.list.page.help.text.publisher')}`}
    </div>
    <div>
      {`${intl.getI18nMessage('label.organization.list.page.filter.by.consumer.types')}:
       ${intl.getI18nMessage('label.organization.list.page.help.text.consumer')}`}
    </div>
  </div>);

const renderTypeHeader = (label, classes, intl) => (
  <div className={classes.helpIconContainer}>
    <div>
      {label}
    </div>
    <Tooltip title={renderTypeHeaderTooltip(intl)} arrow placement="right">
      <HelpIcon className={classes.helpIcon} />
    </Tooltip>
  </div>);

const getOrgListColumns = ({ classes, userContext }, intl) => [{
  id: 'name',
  label: getI18nFormattedMessage('label.organization.name'),
  minWidth: 200,
  ...(hasAdminRole(userContext) ? { link: '/admin/console/organizations/edit' } : {}),
}, {
  id: 'type',
  label: renderTypeHeader(getI18nFormattedMessage('label.organization.type'), classes, intl),
  minWidth: 200,
  value: (item) => formatType(item.type),
}, {
  id: 'accountPlanName',
  label: getI18nFormattedMessage('label.organization.accountplan'),
  minWidth: 100,
}, {
  id: 'status',
  label: getI18nFormattedMessage('label.organization.state'),
  minWidth: 100,
  value: (item) => upperFirst(lowerCase(item.status)),
}];

export const OrganizationList = (props) => {
  const {
    classes,
    userContext,
    isError,
    isLoading,
    totalPages,
    totalElements = 0,
    results = [],
  } = props;

  const intl = getI18n(useIntl());
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [filterByName, setFilterByName] = useState('');
  const [filterByType, setFilterByType] = useState(ANY);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [filterByAnyApplied, setFilterByAnyApplied] = useState(false);
  const [hideAddButton, setHideAddButton] = useState(true);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    props.fetchOrganizations({
      name: filterByName,
      type: filterByType,
      page,
      size: rowsPerPage,
    });
    const isUpdateOrg = localStorage.getItem('isUpdateOrg');
    const isCreateOrg = localStorage.getItem('isCreateOrg');
    if (isUpdateOrg || isCreateOrg) {
      localStorage.removeItem('isUpdateOrg');
      localStorage.removeItem('isCreateOrg');
      const message = isUpdateOrg ?
        intl.getI18nMessage('label.organization.updated.message') :
        intl.getI18nMessage('label.organization.created.message');
      notifyMessages(ALERT_SUCCESS, message);
      // make sure to return the function
      // which clears timer if timer is used
      const timeoutID = window.setTimeout(() => {
        notifyMessages();
      }, 3000);
      return () => window.clearTimeout(timeoutID);
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (!hasPublisherRole(userContext) && !hasOrgPublisherRole(userContext)) {
      props.push('/404');
    }
    if (hasAdminRole(userContext)) {
      setHideAddButton(false);
    }
  }, [userContext]);

  useEffect(() => {
    if (isError) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('error.organization.list.fetch'));
    }
  }, [isError]);

  const onFilterByNameChange = (value) => {
    setFilterByName(value);
  };

  const onFilterByTypeChange = (newType) => {
    setFilterByType(newType);
    props.fetchOrganizations({
      name: filterByName,
      type: newType,
      page,
      size: rowsPerPage,
    });
  };

  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setFilterByAnyApplied(true);
      props.fetchOrganizations({
        name: filterByName,
        type: filterByType,
        page,
        size: rowsPerPage,
      });
    }
  };

  const handleBlur = () => {
    setFilterByAnyApplied(true);
    props.fetchOrganizations({
      name: filterByName,
      type: filterByType,
      page,
      size: rowsPerPage,
    });
  };

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    props.fetchOrganizations({
      name: filterByName,
      type: filterByType,
      page: newPage,
      size: rowsPerPage,
    });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    props.fetchOrganizations({
      name: filterByName,
      type: filterByType,
      page,
      size: rowsPerPage,
    });
  };

  const onAddOrganization = () => {
    props.push('/admin/console/organizations/add');
  };

  const noResultsMessage = filterByAnyApplied ?
    intl.getI18nMessage('label.organization.filter.no.results') :
    intl.getI18nMessage('label.organization.no.results');

  return (
    <ListContainer
      listPageId="organization-list-page"
      isLoading={isLoading}
      notificationId="organization-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationMessage}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      pageHeaderTitle={intl.getI18nMessage('label.organization.list.page.title')}
      addButtonLabel={intl.getI18nMessage('label.organization.list.add.button')}
      onAdd={!hideAddButton && onAddOrganization}
      pageFilterAndSortClass={classes.pageFilterAndSort}
      filterAndSortContent={(
        <Fragment>
          <FilterByName
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByName}
            placeholder={intl.getI18nMessage('label.organization.list.page.filter.by.name.placeholder')}
            handleChange={onFilterByNameChange}
            onKeyPress={onFilterByNameKeyPress}
            handleBlur={handleBlur}
          />
          <FilterByType
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByType}
            placeholder={intl.getI18nMessage('label.gateway.bundle.list.page.filter.by.group.placeholder')}
            handleChange={onFilterByTypeChange}
            hideLabel
          />
        </Fragment>
      )}
      columns={getOrgListColumns(props, intl)}
      rows={results}
      noResultsMessage={noResultsMessage}
      page={page}
      totalElements={totalElements}
      totalPages={totalPages}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onChangePreviousPage={onChangePreviousPage}
      onChangeNextPage={onChangeNextPage}
    />
  );
};

OrganizationList.propTypes = {
  classes: shape({}),
  userContext: shape({}),
  isLoading: bool,
  isError: bool,
  push: func,
  totalPages: number,
  totalElements: number,
  results: arrayOf(shape({})),
};

const mapStateToProps = (state) => ({
  config: getConfig(state),
  userContext: getUserDetails(state),
  isError: getIsListError(state),
  isLoading: getIsLoading(state),
  totalPages: getOrganizationsListTotalPages(state),
  totalElements: getOrganizationsListTotalElements(state),
  results: getOrganizationsListResults(state),
});

const mapDispatchToProps = {
  push,
  fetchOrganizations,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(OrganizationList);



// WEBPACK FOOTER //
// ./src/pages/Organization/List/index.js